import Moment from 'moment';

/**
 * Every time-based figure the tile shows is derived here, from an ISO date and
 * `now`. The prototype stored them -- `daysToRenewal: 179`, `termProgress:
 * 0.51` -- which is wrong the day after the response is cached, and leaves the
 * term bar frozen at whatever fraction was baked in.
 */

/** A renewal is worth talking about this far out. */
export const RENEWAL_WINDOW_DAYS = 60;

/** A quote expiring inside this window gets a warning treatment. */
export const QUOTE_EXPIRY_WARNING_DAYS = 14;

/** ...and a critical one inside this. */
export const QUOTE_EXPIRY_CRITICAL_DAYS = 7;

/**
 * How soon a deadline has to be before it outranks an in-force policy for the
 * hero slot. Without a window, any quote -- which always carries an expiry --
 * would displace the coverage the account actually holds.
 */
export const URGENT_ATTENTION_DAYS = 14;

/**
 * Strict ISO parsing for strings, so an unparseable value fails cleanly rather
 * than going through moment's fallback to `new Date()` -- which guesses, warns,
 * and differs between browsers.
 */
const asMoment = (value: string | number | Date) =>
  typeof value === 'string'
    ? Moment.utc(value, Moment.ISO_8601, true)
    : Moment.utc(value);

const day = (value: string | number | Date) => asMoment(value).startOf('day');

/**
 * Whole days from `now` until `iso`; negative once past. Undefined rather than
 * zero when the date is missing or unparseable -- a fabricated deadline reads
 * as "expires today", which is the one thing absent data must not say.
 */
export function daysUntil(
  iso: string | undefined,
  now: string | number | Date = Date.now()
): number | undefined {
  if (!iso) {
    return undefined;
  }

  const target = day(iso);

  if (!target.isValid()) {
    return undefined;
  }

  return target.diff(day(now), 'days');
}

/** How far through its term a policy is, 0 to 1. */
export function termProgress(
  start: string | undefined,
  end: string | undefined,
  now: string | number | Date = Date.now()
): number | undefined {
  if (!start || !end) {
    return undefined;
  }

  const from = day(start);
  const to = day(end);

  if (!from.isValid() || !to.isValid()) {
    return undefined;
  }

  const span = to.diff(from, 'days');

  if (span <= 0) {
    return undefined;
  }

  const elapsed = day(now).diff(from, 'days');

  return Math.min(1, Math.max(0, elapsed / span));
}

/** Display form. Undefined in, undefined out -- never a placeholder date. */
export function formatCoverageDate(
  iso: string | undefined
): string | undefined {
  if (!iso) {
    return undefined;
  }

  const parsed = asMoment(iso);

  return parsed.isValid() ? parsed.format('MMM D, YYYY') : undefined;
}

/** True once the date is in the past. Absent dates are not past. */
export function isPast(
  iso: string | undefined,
  now: string | number | Date = Date.now()
): boolean {
  const days = daysUntil(iso, now);

  return days !== undefined && days < 0;
}
